import React, { useState } from "react";
import "./CostSupportInfo.css";

type TabKey = "kotsu" | "faq" | "shikumi";

const ChevronDown = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4 6l4 4 4-4" />
  </svg>
);

type AccordionItemProps = {
  id: string;
  badge: React.ReactNode;
  question: string;
  openIds: Set<string>;
  onToggle: (id: string) => void;
  children: React.ReactNode;
};

function AccordionItem({ id, badge, question, openIds, onToggle, children }: AccordionItemProps) {
  const isOpen = openIds.has(id);
  return (
    <div className={`csup-item${isOpen ? " csup-open" : ""}`}>
      <div className="csup-iq" onClick={() => onToggle(id)}>
        {badge}
        <span className="csup-iq-text">{question}</span>
        <span className="csup-chev">
          <ChevronDown />
        </span>
      </div>
      <div className="csup-ia">
        <div className="csup-ia-inner">{children}</div>
      </div>
    </div>
  );
}

type Props = {
  sectionId?: string;
};

export function SettingsSupportInfo({ sectionId = "support-section" }: Props) {
  const [activeTab, setActiveTab] = useState<TabKey>("kotsu");
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="csup-root" id={sectionId}>
      {/* セクションヘッダー */}
      <div className="csup-sec-header">
        <div className="csup-sec-icon">
          <svg viewBox="0 0 20 20">
            <path d="M10 2C5.6 2 2 5.6 2 10s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm1 13H9v-2h2v2zm0-4H9c0-3.25 3-3 3-5 0-1.1-.9-2-2-2s-2 .9-2 2H7c0-1.65 1.35-3 3-3s3 1.35 3 3c0 2-3 2.25-3 5z" />
          </svg>
        </div>
        <span className="csup-sec-title">初期設定 サポート情報</span>
        <span className="csup-sec-date">最終更新：2026年4月</span>
      </div>

      {/* タブ */}
      <div className="csup-tabs">
        <button
          className={`csup-tab csup-tab-kotsu${activeTab === "kotsu" ? " csup-active" : ""}`}
          onClick={() => setActiveTab("kotsu")}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M2 11h12v1.5a1 1 0 01-1 1H3a1 1 0 01-1-1V11zm6-9.5a5 5 0 015 5c0 1.8-1 3.4-2.4 4.3L10 12H6l-.6-1.2A5 5 0 018 1.5zm1 2.3V5h1.5v1.5H9V8H7.5V6.5H6V5h1.5V3.8h1.5z" />
          </svg>
          運用のコツ
        </button>
        <button
          className={`csup-tab csup-tab-faq${activeTab === "faq" ? " csup-active" : ""}`}
          onClick={() => setActiveTab("faq")}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M14 2H2a1 1 0 00-1 1v8a1 1 0 001 1h2v2.5l3-2.5h7a1 1 0 001-1V3a1 1 0 00-1-1zM8 9.5a1 1 0 110-2 1 1 0 010 2zm1-3.3c0 .4-.2.7-.5.9L8 7.4V7h-.5V5.8l.8-.5c.2-.1.2-.2.2-.3 0-.3-.2-.5-.5-.5s-.5.2-.5.5H6c0-.9.7-1.5 1.5-1.5S9 5.3 9 6.2z" />
          </svg>
          よくある質問
        </button>
        <button
          className={`csup-tab csup-tab-shikumi${activeTab === "shikumi" ? " csup-active" : ""}`}
          onClick={() => setActiveTab("shikumi")}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 11.5a1 1 0 110-2 1 1 0 010 2zm.7-4.2c-.4.3-.7.6-.7 1v.2H6.5v-.2c0-.9.5-1.6 1.1-2 .4-.3.6-.6.6-1 0-.6-.5-1-1.2-1-.6 0-1.1.4-1.2.9H4.3C4.5 4.8 5.6 4 7 4c1.6 0 2.7 1 2.7 2.3 0 .9-.5 1.6-1 2z" />
          </svg>
          仕組みを知る
        </button>
      </div>

      {/* 運用のコツ */}
      <div className={`csup-panel${activeTab === "kotsu" ? " csup-active" : ""}`}>
        <AccordionItem
          id="kotsu-1"
          badge={<span className="csup-badge csup-badge-kotsu">コツ</span>}
          question="RMSスタッフ登録は利用開始前に必ず完了させましょう"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>利益Naviをご利用いただくには、RMSへの「スタッフ登録」が必須です。ご契約後すぐに<a href="https://glogin.rms.rakuten.co.jp/" target="_blank" rel="noopener noreferrer">R-Login</a>からスタッフ登録を完了させ、本画面にIDとパスワードを登録してください。</p>
            <p>RMSへの「スタッフ登録」が完了しないと、全てのデータ取得ができず、ツールがご利用いただけません。</p>
          </div>
          <div className="csup-note csup-note-blue">
            RMSスタッフ登録の手順は<a href="https://ec-vision.co.jp/blog/addstaff/" target="_blank" rel="noopener noreferrer">こちら</a>からご確認いただけます。
          </div>
        </AccordionItem>

        <AccordionItem
          id="kotsu-2"
          badge={<span className="csup-badge csup-badge-kotsu">コツ</span>}
          question="R-Loginパスワードは②〜⑤に事前登録しておくと安心です"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>楽天のシステム仕様により、R-Loginパスワードは定期的な変更が必要です。パスワード変更に備えて、次のパスワードを②〜⑤にあらかじめ登録しておくと、変更後もデータ取得が途切れることなく続けられます。</p>
            <p>変更するたびに1つずつ更新していく運用がおすすめです。</p>
          </div>
          <div className="csup-note csup-note-green">
            パスワードは最大5つまで登録可能です。変更のたびに古いものを新しいものに更新していきましょう。
          </div>
        </AccordionItem>

        <AccordionItem
          id="kotsu-3"
          badge={<span className="csup-badge csup-badge-kotsu">コツ</span>}
          question="会計・税設定（今期）は自社の決算期に合わせて設定しましょう"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>会計・税設定（今期）にてご登録いただく開始月は、利益Naviで管理する1年間の「始まりの月」です。自社の決算期や運用開始月に合わせて設定してください。</p>
            <p>設定後は各種画面の年度管理に影響するため、運用開始前に確認しておくことをおすすめします。変更は初期設定ページからいつでも可能です。</p>
          </div>
          <div className="csup-note csup-note-blue">
            開始月を変更すると、月次利益などの表示期間も連動して変わります。
          </div>
        </AccordionItem>
      </div>

      {/* よくある質問 */}
      <div className={`csup-panel${activeTab === "faq" ? " csup-active" : ""}`}>
        <AccordionItem
          id="faq-1"
          badge={<span className="csup-badge csup-badge-faq">Q</span>}
          question="R-Login IDとパスワードが正しいはずなのにエラーになります"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>以下の点をご確認ください。</p>
          </div>
          <ul className="csup-slist">
            <li><span className="csup-snum csup-sn-b">1</span><span>RMSにログインする際の正しい「1回目のIDとパスワード」が入力されているか？</span></li>
            <li><span className="csup-snum csup-sn-b">2</span><span>ec vision株式会社のアカウントのRMSスタッフ登録が完了しているか？</span></li>
            <li><span className="csup-snum csup-sn-b">3</span><span>半角・全角など入力ミスがないか？スペースが最後に入っていないか？</span></li>
          </ul>
          <div className="csup-ia-text" style={{ marginTop: 10 }}>
            <p>RMSの2段階認証の2回目のIDとパスワードは、利益Naviでは登録いたしません。確認後もエラーが続く場合はお問い合わせください。</p>
          </div>
        </AccordionItem>

        <AccordionItem
          id="faq-2"
          badge={<span className="csup-badge csup-badge-faq">Q</span>}
          question="パスワードを変更したら、データが取得されなくなりました"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>RMSのパスワード変更後は、利益Naviの初期設定画面のR-Login ID、R-Login パスワードを最新のものに更新・保存してください。</p>
            <p>②〜⑤に次のパスワードを事前入力しておくと、変更後も自動的に切り替わるためエラーが起きにくくなります。</p>
          </div>
          <div className="csup-note csup-note-green">
            パスワードの事前登録（②〜⑤）を活用することで、変更後もスムーズに連携が続きます。
          </div>
        </AccordionItem>

        <AccordionItem
          id="faq-3"
          badge={<span className="csup-badge csup-badge-faq">Q</span>}
          question="初期設定はどのタイミングで行えばいいですか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>ご契約後すぐに設定することをおすすめします。設定完了後、データの取得が始まり24〜48時間ほどで利益データが反映されます。</p>
            <p>設定が遅れるとその分、利益データの蓄積も遅れます。</p>
          </div>
        </AccordionItem>

        <AccordionItem
          id="faq-4"
          badge={<span className="csup-badge csup-badge-faq">Q</span>}
          question="登録したIDやパスワードは後から変更できますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>はい、初期設定ページからいつでも変更できます。</p>
            <p>変更後は必ず「保存」ボタンを押してください。パスワードのみ変更する場合は、IDはそのままで該当のパスワード欄だけ更新することができます。</p>
          </div>
        </AccordionItem>
      </div>

      {/* 仕組みを知る */}
      <div className={`csup-panel${activeTab === "shikumi" ? " csup-active" : ""}`}>
        <AccordionItem
          id="shikumi-1"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="利益Naviはどうやって楽天の各種費用や受注データを取得していますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>RMSスタッフアカウントを通じて、ご登録いただいたデータから、毎日自動で受注データ・分析データを取得しています。</p>
            <p>そのためRMSへのスタッフ登録と正しいIDの入力が必須です。データは原則、前日分が毎日お昼ごろ（13時ごろ）までに反映されます。</p>
          </div>
        </AccordionItem>

        <AccordionItem
          id="shikumi-2"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="なぜパスワードを複数（②〜⑤）登録できるのですか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>楽天のパスワードポリシーにより定期的なパスワード変更が求められます。変更のたびに利益Naviの設定を更新し忘れると、データ取得が途切れてしまいます。</p>
            <p>②〜⑤に次のパスワードを事前登録しておくことで、変更後も自動的に切り替わり、途切れなくデータを取得し続けられる仕組みです。</p>
          </div>
        </AccordionItem>

        <AccordionItem
          id="shikumi-3"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="開始月を設定するとどのような影響がありますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>開始月は固定費入力の「年度」管理の基準になります。例えば起算月を「4月」に設定すると、4月〜翌3月が1年度として管理されます。</p>
            <p>固定費の年間集計や月次利益の表示にも影響するため、運用開始前に設定しておくことをおすすめします。</p>
          </div>
          <div className="csup-note csup-note-blue">
            開始月の変更は初期設定ページから行えます。
          </div>
        </AccordionItem>

        <AccordionItem
          id="shikumi-4"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="データはどのくらいの頻度で更新されますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>基本的に毎日自動で前日分のデータが取得されます。受注のキャンセルや変更があった場合も、翌日以降の更新で反映されます。リアルタイム反映には対応しておらず、当日分は翌日以降に確認いただく形になります。</p>
            <p>なお、利益Naviではスタンダードプラン以上では、受注データを直近14日間分毎日取得しますので、キャンセル等も14日分は遡って更新されます。</p>
          </div>
        </AccordionItem>

        <AccordionItem
          id="shikumi-5"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="RMSとの接続が切れた場合、データはどうなりますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>パスワード変更などにより接続が切れた場合、その期間中のデータは取得されません。再接続後は以降のデータから取得が再開されますが、接続が切れていた期間のデータは遡って取得できません。</p>
            <p>データの再取得依頼などがあれば、お気軽にお問い合せください。</p>
          </div>
          <div className="csup-note csup-note-amber">
            接続が切れていた期間のデータは店舗様ではご対応いただけません。パスワード変更後はすぐに設定を更新してください。
          </div>
        </AccordionItem>
      </div>
    </div>
  );
}
